import { ProductStoryDeprecated } from "@hermes/api-model-editorial";
import { CrossSellingProduct } from "@hermes/api-model-product";
import {
    GTMMediaType,
    HEROPRODUCT_IMAGE,
    HEROPRODUCT_VIDEO,
    THUMBNAIL_IMAGE,
    THUMBNAIL_VIDEO,
} from "@hermes/fragments/analytics";
import {
    AssetMediaType,
    SCENE7_HERMES_PRODUCT_REGEX,
} from "@hermes/utils-generic/constants";
import {
    formatSkuForImage,
    getMatchAtIndex,
    skuToId,
} from "@hermes/utils-generic/helpers";

import { VIDEO_POSITIONS_IN_GRID } from "../constants/grid.constant";
import { GridAsset, GridData, GridProduct } from "../model/grid.model";

const COLOR_TEXT = $localize`:@@hermes.commerce.product_locator.color.label:Color`;

export const isFromSameFhLocation = (
    incomingUrl: string | undefined,
    existingUrl: string,
): boolean => {
    if (
        !incomingUrl ||
        !existingUrl ||
        incomingUrl.indexOf("fh_location") === 0 ||
        existingUrl.indexOf("fh_location") === 0
    ) {
        return false;
    }
    const incomingFhLocation = incomingUrl
        .split("&")
        .find((framentUrl: string) => framentUrl.includes("fh_location"));
    const existingFhLocation = existingUrl
        .split("&")
        .find((framentUrl: string) => framentUrl.includes("fh_location"));
    return incomingFhLocation === existingFhLocation;
};
export const hasStockRetailOnly = (product: GridProduct): boolean =>
    Boolean(product.stock?.retail) && !product.stock?.ecom;

export const getDimension62 = (assetUrl: string | undefined): string =>
    assetUrl?.includes("composite")
        ? ""
        : getMatchAtIndex(assetUrl, SCENE7_HERMES_PRODUCT_REGEX, 1);

export const getDimension63 = (product: GridProduct): GTMMediaType => {
    const hasVideo = product?.assets ? assetHasVideo(product.assets) : false;
    if (product.isHero) {
        return hasVideo ? HEROPRODUCT_VIDEO : HEROPRODUCT_IMAGE;
    }
    return hasVideo ? THUMBNAIL_VIDEO : THUMBNAIL_IMAGE;
};

export const assetHasVideo = (assets: GridAsset[]): boolean =>
    assets.some((asset) => asset.type === AssetMediaType.Video);

/**
 * Filters the assets of grid products based on specific criteria.
 * @param gridData
 * @returns Filtered grid data.
 */
export const retrieveGridDataWithoutNoDisplayedVideoAssets = (
    gridData: GridData,
): GridData => {
    if (gridData.products.length === 0) {
        return gridData;
    }
    // Get products with filtered assets.
    const filtredProduct = getProductsWithFiltredAssets(gridData.products);
    return {
        ...gridData,
        products: filtredProduct,
    };
};

/**
 * Filters out video assets from grid products
 * @param products
 * @returns The filtered grid products.
 */
export const getProductsWithFiltredAssets = (
    products: GridProduct[],
): GridProduct[] =>
    products.map((product, index) => {
        if (!product.assets) {
            return product;
        }

        product.assets = VIDEO_POSITIONS_IN_GRID.includes(index + 1)
            ? getAssetsReorderVideoPosition(product.assets)
            : getAssetsWithoutVideo(product.assets);
        return product;
    });

/**
 * Filters out video assets from a list of assets.
 * @param assets - The assets to filter.
 * @returns The filtered assets.
 */
export const getAssetsWithoutVideo = (assets: GridAsset[]): GridAsset[] =>
    assets.filter((asset) => asset.type !== AssetMediaType.Video);

export const getAssetsReorderVideoPosition = (
    assets: GridAsset[],
): GridAsset[] => {
    const videoIndex = assets.findIndex(
        (asset) => asset.type === AssetMediaType.Video,
    );
    // Reorder assets with video splitting assets to set video on position 2
    // Example : ["image1", "image2", "image3", "video"] > ["image3", "video", "image1", "image2"]
    if (videoIndex !== -1 && videoIndex !== 1) {
        assets = [
            ...assets.slice(videoIndex - 1),
            ...assets.slice(0, videoIndex - 1),
        ];
    }

    return assets;
};

export const getVariantAsset = (sku: string): string => {
    const imageSku = encodeURIComponent(formatSkuForImage(sku));
    return `${imageSku}_thumbnail_25?defaultimage=${imageSku}_thumbnail_20`;
};

export const skuToGridProductId = (sku: string): string =>
    `product-item-meta-link-${skuToId(sku)}`;

export const getGridTitle = (
    product: GridProduct | CrossSellingProduct | ProductStoryDeprecated,
): string => {
    if (!product.avgColor || product.isColorless) {
        return product.title;
    }

    return `${product.title}, ${product.avgColor}`;
};

export const getVariantLabelPart = (product: ProductStoryDeprecated): string =>
    product.variant && !product.isColorless
        ? `${COLOR_TEXT}: ${product.variant}, `
        : "";

export const gridItemIdFromSku = (sku: string) =>
    `grid-product-${skuToId(sku)}`;
